import apiClient from './config';
import { AuthResponse, LoginCredentials, RegisterData, User } from '../../types/auth';

export interface CompanyChoice {
  companyId: number;
  companyName: string;
  role: string;
  userId: number;
}

export interface LoginResponse {
  requiresCompanySelection?: boolean;
  companyChoices?: CompanyChoice[];
  email?: string;
  token?: string;
  mustChangePassword?: boolean;
  user?: any;
  tokens?: { accessToken: string; refreshToken: string; expiresIn: string };
}

export const authAPI = {
  // The login page shows its own message for a failed sign-in (including the
  // lockout after repeated failures), so the global error toast is skipped.
  login: async (credentials: LoginCredentials & { companyId?: number }): Promise<LoginResponse> => {
    const response = await apiClient.post('/auth/login', credentials, { skipErrorToast: true });
    return response.data.data;
  },

  // Register user
  register: async (data: RegisterData): Promise<AuthResponse['data']> => {
    const response = await apiClient.post('/auth/register', data);
    return response.data.data;
  },

  // Get user profile
  getProfile: async (): Promise<User> => {
    const response = await apiClient.get('/auth/me');
    // Backend returns user data directly in response.data.data (not nested under .user)
    return response.data.data;
  },

  // Update user profile. The PUT response is a bare `{ success, message }`
  // (no user body) so we re-fetch /auth/me afterwards — without this the
  // reducer overwrites the user state with `undefined` and the page falls
  // back to its loading spinner until the next mount.
  updateProfile: async (data: Partial<User> & { avatarUrl?: string }): Promise<User> => {
    await apiClient.put('/auth/me', data);
    const fresh = await apiClient.get('/auth/me');
    return fresh.data.data;
  },

  // Change password
  changePassword: async (data: { currentPassword: string; newPassword: string }): Promise<void> => {
    await apiClient.post('/auth/change-password', data);
  },

  // Refresh token
  refreshToken: async (refreshToken: string): Promise<{ accessToken: string }> => {
    const response = await apiClient.post('/auth/refresh', { refreshToken });
    return response.data.data;
  },

  // Forgot password: emails a single-use reset link. The answer is the same
  // whether or not the account exists. The page shows its own errors.
  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.post('/auth/request-password-reset', { email }, { skipErrorToast: true });
  },

  // Reset password with the token from the emailed link.
  resetPassword: async (data: { token: string; password: string }): Promise<void> => {
    await apiClient.post('/auth/reset-password', data, { skipErrorToast: true });
  },

  // Verify email
  verifyEmail: async (token: string): Promise<void> => {
    await apiClient.post('/auth/verify-email', { token });
  },

  // Resend verification email
  resendVerification: async (email: string): Promise<void> => {
    await apiClient.post('/auth/resend-verification', { email });
  },

  // Logout (optional - mainly for server-side cleanup)
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Ignore errors on logout
      console.warn('Logout API call failed:', error);
    }
  },
};
